import React, { useMemo } from 'react';
import { alpha, Box, Container, Drawer } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import DrawerNavigation from 'src/shared/components/navigations/drawerNavigation';
import NotificationFeed from 'src/shared/components/organisms/notificationFeed';
import ProfileFeed from 'src/shared/components/organisms/profileFeed';
import GroupListFeed from 'src/shared/components/organisms/groupListFeed';
import { ScrollToTop } from 'src/shared/components/atoms/scrollToTop';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Theming
import { useSelector } from 'react-redux';
import { RootStateType } from 'src/shared/services/redux/reducers';
import { ThemeProvider } from '@mui/material/styles';
import { Colors } from 'src/shared/components/styles';
import { createAppTheme } from 'src/shared/theme/createAppTheme';

interface AppTemplateProps {
  mainNavigation: JSX.Element;
  mainContent: JSX.Element;
  showNotificationFeed: boolean;
  setShowNotificationFeed: (arg: boolean) => void;
  showProfileFeed: boolean;
  setShowProfileFeed: (arg: boolean) => void;
  showGroupList: boolean;
  setShowGroupList: (arg: boolean) => void;
  showMenu: boolean;
}

toast.configure({
  position: 'top-right',
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
});

const NAV_WIDTH = 340;
const FEED_WIDTH = 400;

export const AppTemplate = (props: AppTemplateProps) => {
  const {
    mainNavigation,
    mainContent,
    showNotificationFeed,
    setShowNotificationFeed,
    showProfileFeed,
    setShowProfileFeed,
    showGroupList,
    setShowGroupList,
    showMenu,
  } = props;

  const { userCompany } = useSelector(
    (state: RootStateType) => state.authState,
  );

  const theme = useMemo(
    () => createAppTheme(userCompany?.color),
    [userCompany],
  );

  const loading = false;
  const rightFeedOpen =
    showGroupList || showProfileFeed || showNotificationFeed;
  const rightRailTopOffset = (theme.mixins.toolbar.minHeight ?? 64) as number;

  const shellSx = { display: 'flex', height: '100dvh' } as const;
  const leftDrawerSx = {
    display: { xs: 'none', lg: 'block' },
    width: NAV_WIDTH,
    flexShrink: 0,
    zIndex: (t) => t.zIndex.appBar - 1,
    '& .MuiDrawer-paper': {
      width: NAV_WIDTH,
      boxSizing: 'border-box',
      borderRight: `1px solid ${alpha(Colors.GRAY_DARK, 0.08)}`,
      zIndex: (t) => t.zIndex.appBar - 1,
      overflow: 'hidden',
      height: '100dvh',
      display: 'flex',
      flexDirection: 'column',
    },
  } as const;
  const mainSx = {
    flex: 1,
    minWidth: 0,
    display: { xs: showMenu ? 'none' : 'block', lg: 'block' },
    height: '100dvh',
    overflowY: 'auto',
    pr: { lg: rightFeedOpen ? `${FEED_WIDTH}px` : 0 },
  } as const;
  const contentWrapSx = {
    pb: 3,
    p: { xs: 0, md: 2 },
    pt: { xs: 1, md: 2 },
  } as const;
  const rightRailSx = {
    backgroundColor: 'white',
    position: { xs: 'relative', lg: 'fixed' },
    right: { lg: 0 },
    top: { lg: rightRailTopOffset },
    width: { xs: '100%', lg: FEED_WIDTH },
    height: { lg: `calc(100dvh - ${rightRailTopOffset}px)` },
    display: 'flex',
    flexDirection: 'column',
    // Keep the rail under the app bar and modals.
    zIndex: (t) => t.zIndex.appBar - 1,
    borderLeft: { lg: `1px solid ${alpha(Colors.GRAY_DARK, 0.08)}` },
  } as const;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastContainer />
      <ScrollToTop />

      {mainNavigation}

      {/* Shell: viewport-height flex layout; main pane scrolls */}
      <Box sx={shellSx}>
        {/* Left navigation (desktop only) */}
        <Drawer variant='permanent' open sx={leftDrawerSx}>
          {/* No overflowY here — avoids the 2nd scrollbar.
              If DrawerNavigation needs scrolling, it should do it internally. */}
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <DrawerNavigation />
          </Box>
        </Drawer>

        {/* Main content pane (independent scroll) */}
        <Box component='main' sx={mainSx}>
          {/* AppBar spacer: small screens need their own offset in flow */}
          <Box
            sx={(t) => ({
              ...t.mixins.toolbar,
              display: { xs: 'block', lg: 'none' },
            })}
          />
          <Box
            sx={(t) => ({
              ...t.mixins.toolbar,
              display: { xs: 'none', lg: 'block' },
            })}
          />

          <Box sx={contentWrapSx}>
            <Container maxWidth={false} sx={{ maxWidth: 800, mx: 'auto' }}>
              {loading ? (
                <Box
                  sx={{
                    mt: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress color='secondary' />
                </Box>
              ) : (
                mainContent
              )}
            </Container>
          </Box>
        </Box>

        {/* Right rail */}
        {rightFeedOpen && (
          <Box sx={rightRailSx}>
            {/* On small screens the rail is in normal flow, so add an AppBar spacer. */}
            <Box
              sx={(t) => ({
                ...t.mixins.toolbar,
                display: { xs: 'block', lg: 'none' },
              })}
            />
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: { lg: 'auto' },
                pt: { lg: 1 },
              }}
            >
              {showNotificationFeed && (
                <NotificationFeed
                  setShowNotificationFeed={setShowNotificationFeed}
                />
              )}
              {showProfileFeed && (
                <ProfileFeed setShowProfileFeed={setShowProfileFeed} />
              )}
              {showGroupList && (
                <GroupListFeed
                  setShowGroupList={() => setShowGroupList(false)}
                />
              )}
            </Box>
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
};
